import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'

/** Type used for actor circle color. Backend may send this or derive from targetType. */
export type NotificationType =
  | 'NewFollower'
  | 'PostUpvoted'
  | 'CommentOnPost'
  | 'PartApproved'
  | 'PartRejected'
  | 'ModelApproved'
  | 'ModelRejected'
  | 'FlagResolved'
  | 'ReputationChange';

export type NotificationTargetType = 'Post' | 'Part' | 'FirearmModel' | 'Build' | 'Follow';

export interface NotificationItem {
  id: string;
  actorInitials: string;
  actorUsername: string;
  message: string;
  createdAt: string;
  isRead: boolean;
  type?: NotificationType;
  targetType?: NotificationTargetType;
  targetId?: string;
}

export default class NotificationStore {
  unreadCount = 0
  notifications: NotificationItem[] = []
  loading = false
  pageNumber = 1
  hasMore = true

  private _pollInterval: ReturnType<typeof setInterval> | null = null

  constructor () {
    makeAutoObservable(this, { _pollInterval: false })
  }

  /** Start 60-second polling for unread count. Call once after login. */
  startPolling = () => {
    this.fetchUnreadCount()
    if (this._pollInterval) return
    this._pollInterval = setInterval(() => {
      this.fetchUnreadCount()
    }, 60_000)
  }

  stopPolling = () => {
    if (this._pollInterval) {
      clearInterval(this._pollInterval)
      this._pollInterval = null
    }
  }

  fetchUnreadCount = async () => {
    try {
      const count = await agent.Notifications.unreadCount()
      runInAction(() => {
        this.unreadCount = count ?? 0
      })
    } catch {
      // silently fail — don't disrupt UX over a background poll
    }
  }

  loadNotifications = async (params?: { pageNumber?: number; pageSize?: number }) => {
    this.loading = true
    const page = params?.pageNumber ?? 1
    const pageSize = params?.pageSize ?? 20
    try {
      const result = await agent.Notifications.list(page, pageSize)
      runInAction(() => {
        if (page === 1) {
          this.notifications = result.items as NotificationItem[]
        } else {
          this.notifications = [...this.notifications, ...(result.items as NotificationItem[])]
        }
        this.hasMore = result.items.length >= pageSize
        this.pageNumber = page
        this.loading = false
      })
    } catch {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  markAllRead = async () => {
    try {
      await agent.Notifications.markAllRead()
    } catch {
      // best-effort
    }
    runInAction(() => {
      this.notifications = this.notifications.map((n) => ({ ...n, isRead: true }))
      this.unreadCount = 0
    })
  }

  markRead = async (ids: string[]) => {
    // Optimistic update
    const set = new Set(ids)
    let changed = 0
    runInAction(() => {
      this.notifications = this.notifications.map((n) => {
        if (set.has(n.id) && !n.isRead) {
          changed++
          return { ...n, isRead: true }
        }
        return n
      })
      if (changed > 0) this.unreadCount = Math.max(0, this.unreadCount - changed)
    })
    try {
      await agent.Notifications.markRead(ids)
    } catch {
      // best-effort — optimistic update already applied
    }
  }
}
